import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import { useListFullCatalogQuery } from '@/Redux/Slices/catalogApi';
import { useIntegrationInstallationsQuery } from '@/Pages/Integrations/integrationsApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/ui/icon';
import { Switch } from '@/components/ui/switch';
import { EmptyState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import PanelResult from './PanelResult';
import {
  DELIVERY_AGGREGATORS_PERMISSIONS,
  DELIVERY_AGGREGATORS_ROUTES,
  invalidateAggregatorMappings,
  useAggregatorMappingsQuery,
  type AggregatorItemMapping,
  type UpsertMappingBody,
} from './deliveryAggregatorsApi';
import { apiMessage } from './lib';

/**
 * §B4 — external item -> catalogue item, and the marketplace's own sold-out
 * flag. Reached per installation, exactly as the API is: a mapping has no
 * `tenantId` of its own (`AggregatorItemMapping` reaches its tenant through
 * `installation`, same as `integration_credentials` does), so this tab always
 * opens with one installation chosen.
 *
 * An unmapped item is refused at ingestion (`resolveLines`, named by external
 * id in the 400) rather than guessed at, which is the whole reason this
 * screen exists: it is the only way to close that gap without reading a
 * server log.
 */
export default function MappingsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canManage = can(DELIVERY_AGGREGATORS_PERMISSIONS.manageMappings);

  const { data: installationPage, isFetching: loadingInstallations } = useIntegrationInstallationsQuery({
    limit: 200,
    includeUninstalled: false,
  });
  const deliveryInstallations = useMemo(
    () => (installationPage?.items ?? []).filter((i) => i.provider?.category === 'DELIVERY'),
    [installationPage],
  );

  const [installationId, setInstallationId] = useState('');
  const effectiveId = installationId || deliveryInstallations[0]?.id || '';

  const [editing, setEditing] = useState<AggregatorItemMapping | 'new' | null>(null);

  const { data: mappings, isFetching, isError, error, refetch } = useAggregatorMappingsQuery(effectiveId, {
    skip: !effectiveId,
  });

  const { data: catalog } = useListFullCatalogQuery({ isActive: true });
  const catalogNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of catalog?.rows ?? []) map.set(item.id, item.name);
    return map;
  }, [catalog]);

  if (!loadingInstallations && deliveryInstallations.length === 0) {
    return (
      <Card>
        <CardContent className="p-0">
          <EmptyState
            title={t('deliveryAggregators.mappings.noInstallationsTitle')}
            hint={t('deliveryAggregators.mappings.noInstallationsHint')}
            action={
              <Button variant="outline" size="sm" onClick={() => navigate('/integrations/providers')}>
                {t('deliveryAggregators.mappings.goToIntegrations')}
              </Button>
            }
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Field label={t('deliveryAggregators.mappings.installation')} controlId="agg-installation">
          <Select
            value={effectiveId}
            onChange={(e) => setInstallationId(e.target.value)}
            className="w-72"
          >
            {deliveryInstallations.map((i) => (
              <option key={i.id} value={i.id}>
                {i.provider?.name ?? i.providerKey} — {i.branch?.name ?? t('deliveryAggregators.mappings.wholeVenue')}
              </option>
            ))}
          </Select>
        </Field>
        {canManage && effectiveId && (
          <Button onClick={() => setEditing('new')}>
            <Icon as={Plus} />
            {t('deliveryAggregators.mappings.addMapping')}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <PanelResult
            loading={isFetching}
            error={isError ? apiMessage(error, t('deliveryAggregators.states.loadFailed')) : null}
            count={mappings?.length ?? 0}
            onRetry={refetch}
            emptyTitle={t('deliveryAggregators.mappings.emptyTitle')}
            emptyHint={t('deliveryAggregators.mappings.emptyHint')}
            emptyAction={
              canManage && (
                <Button size="sm" onClick={() => setEditing('new')}>
                  {t('deliveryAggregators.mappings.addMapping')}
                </Button>
              )
            }
          >
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('deliveryAggregators.mappings.externalItem')}</TH>
                    <TH>{t('deliveryAggregators.mappings.catalogItem')}</TH>
                    <TH>{t('deliveryAggregators.mappings.available')}</TH>
                    <TH />
                  </TR>
                </THead>
                <tbody>
                  {(mappings ?? []).map((m) => (
                    <MappingRow
                      key={m.id}
                      mapping={m}
                      installationId={effectiveId}
                      catalogName={m.catalogItemId ? (catalogNameById.get(m.catalogItemId) ?? null) : null}
                      canManage={canManage}
                      onEdit={() => setEditing(m)}
                      onReload={refetch}
                    />
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </PanelResult>
        </CardContent>
      </Card>

      {editing && effectiveId && (
        <MappingDialog
          installationId={effectiveId}
          mapping={editing === 'new' ? null : editing}
          catalogItems={catalog?.rows ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function MappingRow({
  mapping,
  installationId,
  catalogName,
  canManage,
  onEdit,
  onReload,
}: {
  mapping: AggregatorItemMapping;
  installationId: string;
  catalogName: string | null;
  canManage: boolean;
  onEdit: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);

  const toggleAvailability = async (next: boolean) => {
    setBusy(true);
    try {
      await api.post(DELIVERY_AGGREGATORS_ROUTES.mappingAvailability(installationId, mapping.externalItemId), {
        isAvailable: next,
      });
      toast.success(
        t(next ? 'deliveryAggregators.mappings.markedAvailable' : 'deliveryAggregators.mappings.markedSoldOut'),
      );
      dispatch(invalidateAggregatorMappings(installationId));
      onReload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('deliveryAggregators.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TR>
      <TD>
        <p className="font-medium">{mapping.externalItemName ?? mapping.externalItemId}</p>
        <p className="text-xs text-ink-muted">{mapping.externalItemId}</p>
      </TD>
      <TD>
        {mapping.catalogItemId ? (
          <span>{catalogName ?? mapping.catalogItemId}</span>
        ) : (
          <Badge tone="danger">{t('deliveryAggregators.mappings.unmapped')}</Badge>
        )}
      </TD>
      <TD>
        <Switch
          checked={mapping.isAvailable}
          onChange={(next) => void toggleAvailability(next)}
          disabled={!canManage || busy}
          aria-label={t('deliveryAggregators.mappings.available')}
        />
      </TD>
      <TD>
        {canManage && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            {t('common.edit')}
          </Button>
        )}
      </TD>
    </TR>
  );
}

function MappingDialog({
  installationId,
  mapping,
  catalogItems,
  onClose,
  onSaved,
}: {
  installationId: string;
  mapping: AggregatorItemMapping | null;
  catalogItems: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [externalItemId, setExternalItemId] = useState(mapping?.externalItemId ?? '');
  const [externalItemName, setExternalItemName] = useState(mapping?.externalItemName ?? '');
  const [catalogItemId, setCatalogItemId] = useState(mapping?.catalogItemId ?? '');
  const [catalogVariantId, setCatalogVariantId] = useState(mapping?.catalogVariantId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!externalItemId.trim()) {
      setError(t('deliveryAggregators.mappings.externalItemRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: UpsertMappingBody = {
        externalItemId: externalItemId.trim(),
        externalItemName: externalItemName.trim() || undefined,
        catalogItemId: catalogItemId || undefined,
        catalogVariantId: catalogVariantId.trim() || undefined,
      };
      await api.post(DELIVERY_AGGREGATORS_ROUTES.mappings(installationId), body);
      toast.success(t('deliveryAggregators.mappings.saved'));
      dispatch(invalidateAggregatorMappings(installationId));
      onSaved();
    } catch (requestError) {
      setError(apiMessage(requestError, t('deliveryAggregators.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={mapping ? t('deliveryAggregators.mappings.editTitle') : t('deliveryAggregators.mappings.addTitle')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={busy}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label={t('deliveryAggregators.mappings.externalItemId')} controlId="map-external-id">
          <Input
            value={externalItemId}
            onChange={(e) => setExternalItemId(e.target.value)}
            disabled={Boolean(mapping)}
            placeholder={t('deliveryAggregators.mappings.externalItemIdHint')}
          />
        </Field>
        <Field label={t('deliveryAggregators.mappings.externalItemName')} controlId="map-external-name">
          <Input
            value={externalItemName}
            onChange={(e) => setExternalItemName(e.target.value)}
            placeholder={t('deliveryAggregators.mappings.externalItemNameHint')}
          />
        </Field>
        <Field label={t('deliveryAggregators.mappings.catalogItem')} controlId="map-catalog-item">
          <Select value={catalogItemId} onChange={(e) => setCatalogItemId(e.target.value)}>
            <option value="">{t('deliveryAggregators.mappings.chooseCatalogItem')}</option>
            {catalogItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('deliveryAggregators.mappings.catalogVariantId')} controlId="map-catalog-variant">
          <Input
            value={catalogVariantId}
            onChange={(e) => setCatalogVariantId(e.target.value)}
            placeholder={t('deliveryAggregators.mappings.catalogVariantIdHint')}
          />
        </Field>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </Dialog>
  );
}
